import { Card } from '../components/Card'
import { CopyButton } from '../components/CopyButton'
import { ToastProvider } from '../components/Toast'
import { AgencySignupApprovalCard } from '../components/admin/AgencySignupApprovalCard'
import { useDb } from '../state/DbProvider'
import {
  agenciesForSuperAgency,
  connectorsForAgency,
  connectorsVisibleToPartner,
  findUser,
  getCurrentUser,
} from '../state/selectors'
import { buildAgencyInviteLink, buildSuperAgencyAgencyInviteLink } from '../utils/affiliate'

function PartnersInner() {
  const { db } = useDb()
  if (!db) return null

  const me = getCurrentUser(db)
  if (me.role !== '総代理店') {
    return (
      <Card title="パートナー管理" subtitle="このページは総代理店向けです">
        <div className="px-5 py-4 text-sm text-slate-600">権限がありません。</div>
      </Card>
    )
  }

  const inviteCode = me.agency?.inviteCode ?? ''
  const connectorInviteLink = inviteCode ? buildAgencyInviteLink(inviteCode) : ''
  const agencyInviteLink = inviteCode ? buildSuperAgencyAgencyInviteLink(inviteCode) : ''
  const subordinateAgencies = agenciesForSuperAgency(db, me.id)
  const directConnectors = connectorsForAgency(db, me.id)
  const managedConnectors = connectorsVisibleToPartner(db, me.id)

  return (
    <div className="grid gap-4">
      <Card title="招待リンク" subtitle="直属コネクター招待と配下代理店申請の両方を管理できます。">
        <div className="grid gap-4 px-5 py-4">
          <InviteLinkRow
            title="コネクター招待リンク"
            inviteCode={inviteCode}
            link={connectorInviteLink}
            description="このリンクから登録したユーザーは総代理店直属のコネクターになります。"
          />
          <InviteLinkRow
            title="代理店申請招待リンク"
            inviteCode={inviteCode}
            link={agencyInviteLink}
            description="このリンクからの代理店申請は、所属総代理店が自動で固定されます。"
          />
        </div>
      </Card>

      <AgencySignupApprovalCard />

      <Card title="配下代理店" subtitle="総代理店配下の代理店一覧です。">
        <div className="grid gap-3 px-5 py-4">
          {subordinateAgencies.map((agency) => (
            <div key={agency.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="font-semibold text-slate-900">{agency.name}</div>
              <div className="mt-1 text-xs text-slate-500">{agency.email}</div>
              <div className="mt-2 text-sm text-slate-600">
                直属コネクター: {connectorsForAgency(db, agency.id).length}人
              </div>
            </div>
          ))}
          {subordinateAgencies.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              配下代理店はまだいません。
            </div>
          ) : null}
        </div>
      </Card>

      <Card title="コネクター一覧" subtitle="直属コネクターと配下代理店配下のコネクターを確認できます。">
        <div className="grid gap-3 px-5 py-4">
          {managedConnectors.map((connector) => {
            const parent = findUser(db, connector.connector?.agencyId)
            const isDirect = parent?.id === me.id

            return (
              <div key={connector.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-slate-900">{connector.name}</div>
                    <div className="mt-1 text-xs text-slate-500">{connector.email}</div>
                  </div>
                  <div className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
                    {isDirect ? '直属' : '配下代理店配下'}
                  </div>
                </div>
                <div className="mt-2 text-sm text-slate-600">
                  所属先: {parent?.name ?? '未設定'}
                </div>
              </div>
            )
          })}
          {managedConnectors.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              コネクターはまだいません。
            </div>
          ) : null}
        </div>
      </Card>

      <Card title="直属コネクター" subtitle="総代理店コードで直接登録されたコネクターです。">
        <div className="grid gap-3 px-5 py-4">
          {directConnectors.map((connector) => (
            <div key={connector.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="font-semibold text-slate-900">{connector.name}</div>
              <div className="mt-1 text-xs text-slate-500">{connector.email}</div>
            </div>
          ))}
          {directConnectors.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              直属コネクターはまだいません。
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  )
}

function InviteLinkRow(props: {
  title: string
  inviteCode: string
  link: string
  description: string
}) {
  return (
    <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-sm font-semibold text-slate-900">{props.title}</div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">招待コード</span>
        <span className="rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-900">
          {props.inviteCode || '-'}
        </span>
        <CopyButton value={props.inviteCode} label="コードをコピー" copiedLabel="コピー済み" />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          readOnly
          value={props.link}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs"
        />
        <CopyButton
          value={props.link}
          label="リンクをコピー"
          copiedLabel="コピー済み"
          className="shrink-0"
        />
      </div>
      <div className="text-xs text-slate-600">{props.description}</div>
    </div>
  )
}

export default function PartnersPage() {
  return (
    <ToastProvider>
      <PartnersInner />
    </ToastProvider>
  )
}
